import { ICurrentUser } from '@api'
import {
  IUsageType,
  ISpaceType,
  IBookingManagerSetting,
  ISpace,
  IDepartment,
  ISettings,
  IEmployee,
  IBooking,
} from '@api/types'
import { get, some } from 'lodash'

import {
  hasDeptManageEmpRights,
  hasDeptManageRights,
  hasDeptUserRights,
  hasLocationManageRights,
  isBookingManager,
  isDepartmentAdmin,
  isSpaceCaptain,
} from './auth'
import { DateUtil } from './dateUtil'
import QueryString from 'qs'
import { Toaster } from '@ui'
import { hotelingDesk, neighborhoodDesk } from '@ui/canvas/utils'

export const OR = 'OR'
export const AND = 'AND'
export const HOT = 'hot_desk'
export const HOTELING = 'hoteling_desk'
export const NEIGHBORHOOD = 'neighborhood'
export const RESERVED = 'reserved_desk'
export const PRIMARY = 'primary_seat'
export const SECONDARY = 'secondary_seat'
export const SHARED = 'shared_desk'

export const isHot = (usage: IUsageType | null) => usage?.name === HOT
export const isHoteling = (usage: IUsageType | null) => usage?.name === HOTELING
export const isNeighborhood = (usage: IUsageType | null) => usage?.name === NEIGHBORHOOD
export const isReserved = (usage: IUsageType | null) => usage?.name === RESERVED
export const isPrimary = (usage: IUsageType | null) => usage?.name === PRIMARY
export const isSecondary = (usage: IUsageType | null) => usage?.name === SECONDARY
export const isShared = (usage: IUsageType | null) => usage?.name === SHARED
export const isPin = (space: ISpace | null) => !!space?.globalMapObject
export const isMeetingRoom = (spaceType: ISpaceType | null) => spaceType?.name.match(/meeting_/)
export const isUnallocatable = (spaceType: ISpaceType | null) => spaceType?.interactivityType === 0

export const HOT_COLOR = 'tomato'
export const HOTELING_COLOR = '#39d2ce'
export const PRIMARY_COLOR = '#eee'
export const SECONDARY_COLOR = '#efd757'
export const SHARED_COLOR = '#e01e84'
export const RESERVED_COLOR = '#3676c1'
export const NEIGHBORHOOD_COLOR = '#AF85FA'

export function getSpaceUsageColor(usageType: IUsageType) {
  switch (usageType.name) {
    case HOT:
      return HOT_COLOR
    case HOTELING:
      return HOTELING_COLOR
    case NEIGHBORHOOD:
      return NEIGHBORHOOD_COLOR
    case PRIMARY:
      return PRIMARY_COLOR
    case RESERVED:
      return RESERVED_COLOR
    case SECONDARY:
      return SECONDARY_COLOR
    case SHARED:
      return SHARED_COLOR
  }
}

export const bookableDesks = [
  'hoteling_desk',
  'neighborhood',
  'primary_seat',
  'secondary_seat',
  'shared_desk',
]

export const hasSpaceEnforceQrCode = (space: ISpace | undefined) => {
  return (
    get(space, 'floor.printableQrCodeUsageTypes', [] as string[]).includes(
      get(space, 'usageType.name', '')
    ) && get(space, 'floor.enforceQrCode', false)
  )
}
export const isSeatable = (space: any, accessList: string[]) =>
  (isShared(space.usageType) && accessList?.includes(SHARED)) ||
  (isHoteling(space.usageType) && accessList?.includes(HOTELING)) ||
  (isSecondary(space.usageType) && accessList?.includes(SECONDARY)) ||
  (isPrimary(space.usageType) && accessList?.includes(PRIMARY)) ||
  (isNeighborhood(space.usageType) && accessList?.includes(NEIGHBORHOOD))

export const spaceEmployees = (space: ISpace) => {
  return (
    (((isShared(space.usageType) && space.spaceAssignment?.sharedEmployees) ||
      ((isSecondary(space.usageType) || isPrimary(space.usageType)) &&
        space.spaceAssignment?.employee && [space.spaceAssignment?.employee]) ||
      (isNeighborhood(space.usageType) &&
        space.spaceAssignment?.neighborhood &&
        space.spaceAssignment?.neighborhood?.employees)) as unknown as IEmployee[]) ?? []
  )
}

export const bookingsManagerBookableSpaceEmployees = (
  space: ISpace,
  bookingManagerSetting?: IBookingManagerSetting
) => {
  const { manage_all = false, allManagableEmployees = [] } = { ...bookingManagerSetting }
  const employeeListIds = allManagableEmployees?.map((emp) => emp.id) || []
  if (isHoteling(space.usageType || { id: '', name: '', displayName: '' })) {
    return manage_all ? [] : allManagableEmployees
  }
  if (!spaceEmployees(space).length) {
    return []
  }
  if (isNeighborhood(space.usageType) || isShared(space.usageType)) {
    const emp = (isNeighborhood(space.usageType)
      ? space.spaceAssignment.neighborhood?.employees || []
      : space.spaceAssignment.sharedEmployees || []) as unknown as IEmployee[]
    return manage_all ? emp : emp.filter((e) => employeeListIds.includes(e.id))
  }
  if (isPrimary(space.usageType) || isSecondary(space.usageType)) {
    return (manage_all
      ? [space.spaceAssignment.employee]
      : employeeListIds.includes(space.spaceAssignment.employee?.id || '')
      ? [space.spaceAssignment.employee]
      : []) as unknown as IEmployee[]
  }
  return []
}

const isSpaceBookableBySpaceCaptain = (user: ICurrentUser, space: ISpace) =>
  isSpaceCaptain(user.role)
    ? hasLocationManageRights(user?.employee, space.floor.id)
      ? space.isBookableUsageType || space.isBookableByEmployee
      : space.isBookableByEmployee
    : true

export const isManagedByUser = (space: ISpace, user: ICurrentUser, isAdminRole: boolean) => {
  let isManageable: boolean | null | undefined = false
  if (isBookingManager(user.role)) {
    isManageable = isHoteling(space.usageType)
      ? true
      : bookingsManagerBookableSpaceEmployees(space, user?.bookingManagerSetting).length > 0
  } else if (isAdminRole) {
    if (neighborhoodDesk(space.usageType)) {
      isManageable =
        !!spaceEmployees(space)?.length &&
        (isSpaceCaptain(user.role)
          ? isSpaceBookableBySpaceCaptain(user, space)
          : space.isBookableUsageType)
    } else if (
      (isDepartmentAdmin(user.role) || isSpaceCaptain(user.role)) &&
      space.spaceAssignment &&
      space.spaceAssignment.department
    ) {
      const department = space?.spaceAssignment?.department
      const sharedDept = space?.spaceAssignment?.sharedDepartments
      if (hotelingDesk(space.usageType)) {
        return true
      } else {
        isManageable =
          department &&
          (hasDeptUserRights(user?.employee, space) ||
            hasDeptManageRights(user?.employee, space) ||
            (sharedDept && hasDeptManageEmpRights(user?.employee, sharedDept)))

        isManageable = isManageable && isSpaceBookableBySpaceCaptain(user, space)
      }
    } else {
      isManageable = space.isBookableUsageType
    }
    //for Viewer and IT role
  } else {
    isManageable = space.isBookableByEmployee
  }
  return isManageable
}

export const getUsageTypesByLocations = (
  bookableLocations: any,
  { state, country, city }: { state: string; country: string; city: string }
) =>
  bookableLocations.reduce(
    (a: any, b: any) => [
      ...a,
      ...(b.locations.some(
        (l: any) => l.state === state && l.country === country && l.city === city
      )
        ? b.bookableSpaceTypes
        : []),
    ],
    []
  )

export const shareBookingSpace = (booking: IBooking) => {
  const {
    id,
    startDate,
    endDate,
    space: {
      floor: {
        id: floorId,
        building: { timezone },
      },
      id: spaceId,
    },
  } = booking
  const start = DateUtil.utc(startDate, timezone)
  const end = DateUtil.utc(endDate, timezone)
  const deepLink = encodeURIComponent(
    btoa(
      QueryString.stringify({
        bookingId: id,
      })
    )
  )

  const link = `${
    window.location.origin
  }/floor_plan/${floorId}/${spaceId}?from=${encodeURIComponent(
    start.toISOString()
  )}&to=${encodeURIComponent(end.toISOString())}&deep_link=${deepLink}`

  navigator.clipboard.writeText(link)
  Toaster.success({ message: 'Link copied.' })
}

export const departmentStr = (department: IDepartment | null, team: IDepartment | null) => {
  if (team && department) {
    return `${department.name} | ${team.name}`
  } else if (department) {
    return `${department.name}`
  } else {
    return ''
  }
}

export const bookingRestricted = ({
  user,
  space: {
    spaceAssignment: { department, team },
    workplaceGroup,
  },
  settings,
  deskType,
}: {
  user: ICurrentUser | null
  space: ISpace
  settings: ISettings | any
  deskType: string
}) => {
  const userAndSpaceDeptSame = team
    ? get(team, 'id', '') === get(user, 'team.id')
    : get(department, 'id', '') === get(user, 'dept.id')

  const userWorkplaceGroups = get(user, 'workplaceGroups', [])
  const currentWorkplaceGroupId = get(workplaceGroup, 'id', '')
  const userAndSpaceWpgSame = some(userWorkplaceGroups, {
    id: currentWorkplaceGroupId,
  })
  // only dept restrictions
  if (
    settings.hotelingDeskDeptEnforcementEnabled &&
    !settings.hotelingDeskWpgEnforcementEnabled &&
    department &&
    !userAndSpaceDeptSame
  ) {
    return `This ${deskType} is restricted to ${departmentStr(department, team)} only`
  }

  // only wpg restrictions
  if (
    !settings.hotelingDeskDeptEnforcementEnabled &&
    settings.hotelingDeskWpgEnforcementEnabled &&
    workplaceGroup &&
    !userAndSpaceWpgSame
  )
    return `This ${deskType} is restricted to ${workplaceGroup.name} only`

  // wpg AND department
  if (
    settings.hotelingDeskDeptEnforcementEnabled &&
    settings.hotelingDeskWpgEnforcementEnabled &&
    settings.hotelingDeskWpgEnforcementOperator === AND &&
    ((department && !userAndSpaceDeptSame) || (workplaceGroup && !userAndSpaceWpgSame))
  ) {
    return `This ${deskType} is restricted to ${departmentStr(department, team)} ${
      department && workplaceGroup ? 'and' : ''
    } ${workplaceGroup ? `${workplaceGroup.name}` : ''} only`
  }

  // wpg OR department
  if (
    settings.hotelingDeskDeptEnforcementEnabled &&
    settings.hotelingDeskWpgEnforcementEnabled &&
    settings.hotelingDeskWpgEnforcementOperator === OR &&
    department &&
    workplaceGroup &&
    !(userAndSpaceWpgSame || userAndSpaceDeptSame)
  ) {
    return `This ${deskType} is restricted to ${departmentStr(department, team)} ${
      department && workplaceGroup ? 'or' : ''
    } ${workplaceGroup ? `${workplaceGroup.name}` : ''} only`
  }
}
